import { nowInstant } from '@insio/dates';

const STORAGE_KEY = 'insio:chunk-reload';
const COOLDOWN_MS = 60_000;
// Leaving aborts the page's pending fetches (Safari fails them), so their chunks are not stale.
const LEAVING_WINDOW_MS = 10_000;

let leavingAt: number | null = null;

/** True for stale-chunk load failures (hashed chunk gone after a deploy). */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return (
    error.name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(error.message) ||
    /error loading dynamically imported module/i.test(error.message) ||
    /Importing a module script failed/i.test(error.message)
  );
}

interface ReloadGuard {
  url: string;
  at: number;
}

function isReloadGuard(value: unknown): value is ReloadGuard {
  return (
    typeof value === 'object' &&
    value !== null &&
    'url' in value &&
    typeof value.url === 'string' &&
    'at' in value &&
    typeof value.at === 'number'
  );
}

/** Reloads at most once per URL per cooldown window. Returns true if a reload was triggered. */
export function reloadOnceForStaleChunk(): boolean {
  const url = window.location.href;
  const now = nowInstant().epochMilliseconds;

  // A reload now would replace the page the user is navigating to with this one.
  if (leavingAt !== null && now - leavingAt < LEAVING_WINDOW_MS) {
    return false;
  }

  let last: ReloadGuard | null = null;
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null');
    if (isReloadGuard(parsed)) {
      last = parsed;
    }
  } catch {
    last = null;
  }

  if (last && last.url === url && now - last.at < COOLDOWN_MS) {
    return false;
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ url, at: now }));
  } catch {
    // sessionStorage unavailable — still reload, the guard just won't persist
  }
  window.location.reload();
  return true;
}

/** Reloads the page once when Vite fails to fetch a lazy route chunk after a deploy. */
export function installPreloadErrorReload(): void {
  window.addEventListener('beforeunload', () => {
    leavingAt = nowInstant().epochMilliseconds;
  });
  window.addEventListener('pageshow', () => {
    leavingAt = null;
  });
  window.addEventListener('vite:preloadError', event => {
    if (reloadOnceForStaleChunk()) {
      event.preventDefault();
    }
  });
}
